import React, { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Info, AlertTriangle, X } from 'lucide-react';
import { toastService, Toast, ToastType } from '../services/toastService';

const ToastContainer: React.FC = () => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = toastService.subscribe(setToasts);
    return unsubscribe;
  }, []);

  const getIcon = (type: ToastType) => {
    switch (type) {
      case 'success':
        return <CheckCircle className="w-5 h-5" />;
      case 'error':
        return <XCircle className="w-5 h-5" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'info':
        return <Info className="w-5 h-5" />;
    }
  };

  const getColors = (type: ToastType) => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-bg-card border-success',
          text: 'text-txt',
          icon: 'text-success',
          progress: 'bg-success'
        };
      case 'error':
        return {
          bg: 'bg-bg-card border-danger',
          text: 'text-txt',
          icon: 'text-danger',
          progress: 'bg-danger'
        };
      case 'warning':
        return {
          bg: 'bg-bg-card border-warning',
          text: 'text-txt',
          icon: 'text-warning',
          progress: 'bg-warning'
        };
      case 'info':
        return {
          bg: 'bg-bg-card border-accent',
          text: 'text-txt',
          icon: 'text-accent',
          progress: 'bg-accent'
        };
    }
  };

  if (toasts.length === 0) return null;

  return (
    <div
      className="
        fixed z-[9999] flex flex-col gap-2 pointer-events-none
        max-lg:top-[max(5.25rem,calc(env(safe-area-inset-top,0px)+3.5rem+1.25rem))]
        lg:top-32
        right-[max(1rem,env(safe-area-inset-right,0px))]
      "
    >
      {toasts.map((toast) => {
        const colors = getColors(toast.type);
        return (
          <div
            key={toast.id}
            className={`${colors.bg} border ${colors.text} rounded-xl shadow-heavy p-4 min-w-[320px] max-w-md pointer-events-auto animate-in slide-in-from-right duration-300`}
          >
            <div className="flex items-start gap-3">
              <div className={colors.icon}>
                {getIcon(toast.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium break-words">
                  {toast.message}
                </p>
              </div>
              <button
                onClick={() => toastService.remove(toast.id)}
                className={`${colors.icon} hover:opacity-70 transition-opacity flex-shrink-0`}
                aria-label="Close notification"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            {/* Progress bar */}
            {toast.duration > 0 && (
              <div className="mt-2 h-1 bg-bg-surface-hover rounded-full overflow-hidden">
                <div
                  className={`h-full ${colors.progress} animate-progress`}
                  style={{
                    animation: `progress ${toast.duration}ms linear forwards`
                  }}
                />
              </div>
            )}
          </div>
        );
      })}
      <style>{`
        @keyframes progress {
          from { width: 100%; }
          to { width: 0%; }
        }
        .animate-progress {
          animation: progress linear forwards;
        }
      `}</style>
    </div>
  );
};

export default ToastContainer;
